import LinkableMixin from './mixins/LinkableMixin';
import Element from '../ui/Element';
import mixin from '../privates/utils/mixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import AnimatableMixin from './mixins/AnimatableMixin';
import DisabledMixin from './mixins/DisabledMixin';
import ClickableMixin from './mixins/ClickableMixin';
import StyleMixin from './mixins/styleMixins/StyleMixin';
import LabelMixin from './mixins/LabelMixin';

/**
 * @class Button
 * @summary A text button or an icon button.
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.DisabledMixin
 * @mixes $w.LinkableMixin
 * @mixes $w.ClickableMixin
 * @mixes $w.StyleMixin
 * @mixes $w.LabelMixin
 * @memberof $w
 * @viewername wysiwyg.viewer.components.SiteButton
 * @viewername wysiwyg.common.components.imagebutton.viewer.ImageButton
 * @definitionId wysiwyg.viewer.components.SiteButton
 * @definitionId wysiwyg.viewer.components.PopupCloseTextButton
 * @definitionId wysiwyg.viewer.components.PopupCloseIconButton
 * @definitionId wysiwyg.common.components.imagebutton.viewer.ImageButton
 * @definitionId wysiwyg.viewer.components.SiteButton
 * @tagname button
 */
class Button extends Element {

    toJSON() {
        const {id, type, global, rendered, label} = this;
        return {id, type, global, rendered, label};
    }

    //DESCRIPTION OVERRIDES
    /**
     * @member style
     * @label property
     * @syntax
     * get style(): Style
     * @summary Gets an object containing information about the button's styles.
     * @description
     * The following styles can be used with buttons:
     *
     *  + [`backgroundColor`]($w.Style.html#backgroundColor)
     *  + [`borderColor`]($w.Style.html#borderColor)
     *  + [`borderRadius`]($w.Style.html#borderRadius)
     *  + [`borderWidth`]($w.Style.html#borderWidth)
     *  + [`color`]($w.Style.html#color)
     *
     *
     *  Getting or setting a button's styles, gets or sets the styles of the
     *  button's regular state. It does not set the styles of the button's hover
     *  or disabled states.
     * @note
     *  Not all styles can be used on all button designs. To determine which styles
     *  work with a specific button design, go to the **Button Design** panel in
     *  the Editor, choose a design, and click **Customize Design**. The design
     *  options that you see in the **Button Design** panel for your particular
     *  button are the styles you can use in your code.
     * @snippet [BackgroundColorMixin-backgroundColor_set.es6=Set the background color]
     * @snippet [BackgroundColorMixin-backgroundColor_get.es6=Get the background color]
     * @type {$w.Style}
     * @memberof $w.Button
     * @readonly
     * @override
     */

    /**
     * @member label
     * @label property
     * @syntax
     * get label(): string
     * set label(value: string): void
     * @summary Sets or gets the label of a text button.
     * @type {external:String}
     * @snippet [Button-label_get.es6=Get a button's label]
     * @snippet [Button-label_set.es6=Set a button's label]
     * @default Empty string
     * @memberof $w.Button
     * @override
     */

    /**
     * @member label
     * @memberof $w.Button
     * @param {external:String} value
     */

}

mixin(Button.prototype, LinkableMixin.prototype);
mixin(Button.prototype, HiddenCollapsedMixin.prototype);
mixin(Button.prototype, DisabledMixin.prototype);
mixin(Button.prototype, ClickableMixin.prototype);
mixin(Button.prototype, StyleMixin.prototype);
mixin(Button.prototype, LabelMixin.prototype);
mixin(Button.prototype, AnimatableMixin.prototype);

export default Button;
